import * as React from 'react'
import {
  FaIcon,
  faFilterSlash,
  faCircleInfo,
  faDownloadSolid,
  faAddressCardSolid,
  faSteeringWheelSolid,
  faTruckSolid,
  faFileLinesSolid,
  faChartPieSimpleSolid,
} from '@/lib/fa'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import {
  useQueryHistoricalFiles,
  downloadImportedFiles,
} from '@/lib/api/company/fileProcessing/historical'
import { toast } from '@/lib/toasts/api'
import { Source } from '@/app/constants/import/source'
import { FileType } from '@/app/constants/fileType'
import { ProcessedStatus } from '@/app/models/import/processedStatus'

import { useTable } from '@/modules/drivers/context/TableContext'
import { TableHeader } from '@/modules/drivers/shared/tableItems/TableHeader'

import TableTextRow from '@/modules/drivers/shared/tableItems/TableTextRow'
import { useSortableData } from '@/modules/drivers/hooks/useSortTable'
import { TableHeaderColumn } from '@/modules/drivers/types/table'
import { Spinner } from '@/ui/feedback'

import { FieldsetSelect } from '@/modules/settings/components/FieldsetSelect'
import { DateField } from '@/ui/form/controls/DateField'
import { TextField } from '@/ui/form/controls/TextField'

import { FileDetailsDialog } from '@/modules/imports/pages/home/components/history/FileDetailsDialog'
import { HistorySelectedActionBar } from '@/modules/imports/pages/home/components/history/HistorySelectedActionBar'
import {
  flattenGroups,
  formatDateTime,
  matchesDate,
  matchesFileType,
  matchesStatus,
} from '@/modules/imports/pages/home/components/history/utils'
import {
  FileTypeFilter,
  HistoryRow,
  StatusFilter,
} from '@/modules/imports/pages/home/components/history/types'

type Props = {
  companyId: string
  dateFrom: string
  dateTo: string

  driverName?: string
}

const INITIAL_VISIBLE = 25
const VISIBLE_INCREMENT = 25

function matchesQuery(row: HistoryRow, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  const haystack = [row.driver ?? '', row.vehicle ?? '', row.fileName ?? '']
    .join(' ')
    .toLowerCase()
  const tokens = q.split(/\s+/).filter(Boolean)
  return tokens.some((tok) => haystack.includes(tok))
}

function fileTypeIcon(row: HistoryRow): { icon: any; key: string } {
  if (row.source === Source.Card) return { icon: faAddressCardSolid, key: 'history-type-driver-card' }
  if (row.fileType === FileType.Ddd) return { icon: faSteeringWheelSolid, key: 'history-type-vehicle' }
  if (row.fileType === FileType.Gps) return { icon: faTruckSolid, key: 'history-type-gps' }
  if (row.fileType === FileType.Tms) return { icon: faTruckSolid, key: 'history-type-tms' }
  if (row.fileType === FileType.Chart) return { icon: faChartPieSimpleSolid, key: 'history-type-chart' }
  return { icon: faFileLinesSolid, key: 'history-type-other' }
}

function processedBadge(status: ProcessedStatus, t: (k: string) => string) {
  if (status === ProcessedStatus.All) {
    return (
      <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-medium tw-text-success">
        <span className="tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-success" />
        {t('importModule.history-status-compatible')}
      </span>
    )
  }
  if (status === ProcessedStatus.Some) {
    return (
      <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-medium tw-text-warning">
        <span className="tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-warning" />
        {t('importModule.history-status-partial')}
      </span>
    )
  }
  return (
    <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-medium tw-text-error">
      <span className="tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-error" />
      {t('importModule.history-status-incompatible')}
    </span>
  )
}

const CardHistoryTable: React.FC<Props> = ({ companyId, dateFrom, dateTo, driverName }) => {
  const { t } = useLocalize()

  
  const { data: groups, isLoading } = useQueryHistoricalFiles({
    companyId,
    dateFrom,
    dateTo,
    isDriver: false,
  })

  const rows = React.useMemo(() => flattenGroups(groups ?? []), [groups])

  const [dateFilter, setDateFilter] = React.useState<string>('')
  const [fileTypeFilter, setFileTypeFilter] = React.useState<FileTypeFilter>('all')
  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>('all')

  const [search, setSearch] = React.useState<string>(driverName ?? '')
  const prefilledRef = React.useRef(Boolean(driverName))
  React.useEffect(() => {
    if (!prefilledRef.current && driverName) {
      prefilledRef.current = true
      setSearch(driverName)
    }
  }, [driverName])
  const [visibleCount, setVisibleCount] = React.useState(INITIAL_VISIBLE)
  const [detailsTarget, setDetailsTarget] = React.useState<HistoryRow | null>(null)

  const { toggleAll, toggleId, selectedGroup, isSelectedAny, setAllIds, resetAll } = useTable()
  const selectedIds = (selectedGroup?.ids ?? []) as (string | number)[]

  const hasActiveFilters =
    Boolean(dateFilter) || fileTypeFilter !== 'all' || statusFilter !== 'all' || Boolean(search.trim())

  const handleClearFilters = () => {
    setDateFilter('')
    setFileTypeFilter('all')
    setStatusFilter('all')
    setSearch('')
  }

  const filtered = React.useMemo(() => {
    return rows.filter(
      (r) =>
        matchesDate(r, dateFilter) &&
        matchesFileType(r, fileTypeFilter) &&
        matchesStatus(r, statusFilter) &&
        matchesQuery(r, search),
    )
  }, [rows, dateFilter, fileTypeFilter, statusFilter, search])

  const columns = React.useMemo<TableHeaderColumn[]>(
    () => [
      { title: t('importModule.history-col-date'), key: 'importDate', type: 'date', sort: true },
      { title: t('importModule.history-col-name'), key: 'fileName', type: 'string', sort: true },
      { title: t('importModule.history-col-file-type'), key: 'fileType', type: 'string', sort: true },
      {
        title: t('importModule.history-col-driver-vehicle'),
        key: 'driverVehicle',
        type: 'string',
        sort: true,
      },
      { title: t('importModule.history-col-status'), key: 'processedStatus', type: 'string', sort: true },
      { title: '', key: 'actions', type: 'string', sort: false },
    ],
    [t],
  )

  const dataForSort = React.useMemo(
    () =>
      filtered.map((r) => ({
        ...r,
        driverVehicle: r.driver || r.vehicle || '',
      })),
    [filtered],
  )

  const { sortedData, sortConfig, handleHeaderClick } = useSortableData({
    data: dataForSort,
    tableColumns: columns,
    includeActiveSorting: false,
  })

  React.useEffect(() => {
    setAllIds(sortedData.map((r) => ({ id: r.id })))
  }, [sortedData, setAllIds])

  React.useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE)
  }, [dateFilter, fileTypeFilter, statusFilter, search, sortConfig])

  React.useEffect(() => {
    resetAll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFilter, fileTypeFilter, statusFilter, search])

  const total = sortedData.length
  const pageRows = sortedData.slice(0, visibleCount)
  const hasMore = visibleCount < total

  const sentinelRef = React.useRef<HTMLTableRowElement>(null)
  React.useEffect(() => {
    if (!hasMore) return
    const sentinel = sentinelRef.current
    if (!sentinel) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisibleCount((c) => Math.min(c + VISIBLE_INCREMENT, total))
        }
      },
      { threshold: 0.1 },
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasMore, total])

  const [isDownloading, setIsDownloading] = React.useState(false)

  const handleDownload = async (ids: (string | number)[]) => {
    if (isDownloading || ids.length === 0) return
    setIsDownloading(true)
    try {
      await downloadImportedFiles({ companyId, ids })
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[card-history] download failed:', err)
      toast.error({ text: t('importModule.history-download-failed') })
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <div className="tw-bg-white">
    
      {isSelectedAny ? (
        <HistorySelectedActionBar
          selectedCount={selectedIds.length}
          onDownload={() => handleDownload(selectedIds)}
          onClose={resetAll}
        />
      ) : (
        <div className="tw-flex tw-w-full tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-border-b tw-border-[#e5e6e6] tw-bg-[#fafbff] tw-px-4 tw-py-2">
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
            <div className="tw-w-40">
              <DateField
                value={dateFilter}
                setValue={(v) => setDateFilter(v ?? '')}
                placeholder={t('importModule.history-filter-date')}
              />
            </div>

            <div className="tw-w-40">
              <FieldsetSelect
                placeholder={t('importModule.history-filter-file-type')}
                value={fileTypeFilter}
                handleChange={(v) => setFileTypeFilter(v as FileTypeFilter)}
                searchEnabled={false}
                background
                options={[
                  { value: 'all', label: t('importModule.history-filter-all') },
                  { value: 'driverCard', label: t('importModule.history-type-driver-card') },
                  { value: 'vehicle', label: t('importModule.history-type-vehicle') },
                  { value: 'gps', label: t('importModule.history-type-gps') },
                  { value: 'tms', label: t('importModule.history-type-tms') },
                  { value: 'chart', label: t('importModule.history-type-chart') },
                  { value: 'other', label: t('importModule.history-type-other') },
                ]}
              />
            </div>

            <div className="tw-w-52">
              <TextField
                placeholder={t('importModule.history-filter-driver-vehicle')}
                value={search}
                onChange={(e) => setSearch((e.target as HTMLInputElement).value)}
              />
            </div>

            <div className="tw-w-40">
              <FieldsetSelect
                placeholder={t('importModule.history-filter-status')}
                value={statusFilter}
                handleChange={(v) => setStatusFilter(v as StatusFilter)}
                searchEnabled={false}
                background
                options={[
                  { value: 'all', label: t('importModule.history-filter-all') },
                  { value: 'compatible', label: t('importModule.history-status-compatible') },
                  { value: 'partial', label: t('importModule.history-status-partial') },
                  { value: 'incompatible', label: t('importModule.history-status-incompatible') },
                ]}
              />
            </div>

            {hasActiveFilters && (
              <button
                type="button"
                onClick={handleClearFilters}
                title={t('importModule.history-filter-clear')}
                className="tw-inline-flex tw-h-8 tw-items-center tw-gap-2 tw-rounded-md tw-px-2 tw-text-sm tw-font-medium tw-text-neutral hover:tw-bg-base-200"
              >
                <FaIcon icon={faFilterSlash} />
                {t('importModule.history-filter-clear')}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="tw-overflow-x-auto">
        <table className="tw-w-full tw-table-auto tw-border-collapse">
          <TableHeader
            columns={columns}
            disabledCheckboxToggle
            fontSize="md"
            sortConfig={sortConfig}
          />
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={columns.length + 1} className="tw-px-4 tw-py-10 tw-text-center tw-text-sm tw-text-neutral">
                  {t('common.loading')}
                </td>
              </tr>
            )}
            {!isLoading && pageRows.length === 0 && (
              <tr>
                <td colSpan={columns.length + 1} className="tw-px-4 tw-py-10 tw-text-center tw-text-sm tw-text-neutral">
                  {t('importModule.history-empty')}
                </td>
              </tr>
            )}
            {!isLoading &&
              pageRows.map((row) => {
                const typeInfo = fileTypeIcon(row)
                const isChecked = selectedIds.includes(row.id)
                return (
                  <tr
                    key={row.id}
                    className={`notches-border--right tw-cursor-pointer tw-border-b tw-bg-white hover:tw-bg-primary/10 ${
                      isChecked ? 'tw-bg-primary/10 tw-font-medium' : ''
                    }`}
                  >
                  
                    <TableTextRow>{formatDateTime(row.importDate)}</TableTextRow>
                    <TableTextRow>{row.fileName}</TableTextRow>
                    <TableTextRow icon={<FaIcon icon={typeInfo.icon} />}>
                      {t(`importModule.${typeInfo.key}`)}
                    </TableTextRow>
                    <TableTextRow>
                      {row.driver || row.vehicle || (
                        <span className="tw-text-neutral">—</span>
                      )}
                    </TableTextRow>
                    <TableTextRow>{processedBadge(row.processedStatus, t)}</TableTextRow>
                
                  </tr>
                )
              })}
            {hasMore && (
              <tr ref={sentinelRef}>
                <td colSpan={columns.length + 1} className="tw-px-4 tw-py-4 tw-text-center">
                  <Spinner className="tw-h-5 tw-w-5 tw-text-neutral" />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {detailsTarget && (
        <FileDetailsDialog
          companyId={companyId}
          row={detailsTarget}
          onClose={() => setDetailsTarget(null)}
          onDownload={(id) => handleDownload([id])}
        />
      )}
    </div>
  )
}

export default CardHistoryTable
